import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotificationContext } from '../context/NotificationContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import {
  MessageSquare,
  Send,
  ArrowLeft,
  Loader2,
  User,
  UtensilsCrossed,
  Clock,
  Check,
  CheckCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated } = useAuth();
  const { refreshUnreadCount, playNotificationSound } = useNotificationContext();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const previousMessageCountRef = useRef(0);

  // Check for conversationId in URL params
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === convId);
      if (conv) {
        setSelectedConversation(conv);
      }
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      previousMessageCountRef.current = 0; // Reset when selecting new conversation
    }
  }, [selectedConversation]);

  // Poll for new messages when in a conversation
  useEffect(() => {
    if (!selectedConversation || !token) return;
    
    const pollInterval = setInterval(() => {
      fetchMessages(selectedConversation.id);
    }, 10000); // Poll every 10 seconds when viewing a conversation
    
    return () => clearInterval(pollInterval);
  }, [selectedConversation, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
      // Refresh global unread count
      refreshUnreadCount();
    } catch (err) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API_URL}/api/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for new messages and play sound
      if (res.data.length > previousMessageCountRef.current && previousMessageCountRef.current > 0) {
        const newMsgs = res.data.slice(previousMessageCountRef.current);
        const hasNewFromOther = newMsgs.some(m => m.sender_id !== user?.id);
        if (hasNewFromOther && playNotificationSound) {
          playNotificationSound();
        }
      }
      previousMessageCountRef.current = res.data.length;
      
      setMessages(res.data);
      // Update unread count in conversation list
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
      // Refresh global unread count
      refreshUnreadCount();
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const recipientId = selectedConversation.host_id === user.id 
        ? selectedConversation.guest_id 
        : selectedConversation.host_id;

      await axios.post(`${API_URL}/api/messages`, {
        experience_id: selectedConversation.experience_id,
        recipient_id: recipientId,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getOtherParticipantName = (conv) => {
    return conv.host_id === user?.id ? conv.guest_name : conv.host_name;
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]">
      <Navbar />
      <Toaster />
      <div className="h-20" />
      
      <main className="flex-1 max-w-6xl mx-auto px-4 py-6 w-full">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-4 border-b bg-gray-50">
                <h1 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#D65A31]" />
                  Messages
                </h1>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No messages yet</p>
                    <p className="text-gray-400 text-xs mt-1">
                      Start a conversation by messaging a host about an experience
                    </p>
                  </div>
                ) : (
                  conversations.map(conv => (
                    <div
                      key={conv.id}
                      data-testid={`conversation-${conv.id}`}
                      onClick={() => setSelectedConversation(conv)}
                      className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-orange-50 border-l-4 border-l-[#D65A31]' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {conv.experience_image ? (
                            <img src={conv.experience_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <UtensilsCrossed className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm truncate">
                              {getOtherParticipantName(conv)}
                            </span>
                            {conv.last_message_at && (
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {formatTime(conv.last_message_at)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{conv.experience_title}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-sm text-gray-600 truncate">
                              {conv.last_message || 'No messages yet'}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="bg-[#D65A31] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
                    <button 
                      onClick={() => setSelectedConversation(null)}
                      className="md:hidden p-2 hover:bg-gray-200 rounded-full"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {selectedConversation.experience_image ? (
                        <img src={selectedConversation.experience_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold truncate">
                        {getOtherParticipantName(selectedConversation)}
                      </h2>
                      <p className="text-xs text-gray-500 truncate">
                        Re: {selectedConversation.experience_title}
                      </p>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-500 text-sm">No messages in this conversation yet</p>
                        <p className="text-gray-400 text-xs mt-1">Send a message to get started</p>
                      </div>
                    ) : (
                      messages.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] ${
                            msg.sender_id === user?.id 
                              ? 'bg-[#D65A31] text-white rounded-2xl rounded-br-md' 
                              : 'bg-white border rounded-2xl rounded-bl-md'
                          } px-4 py-2 shadow-sm`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-1 ${
                              msg.sender_id === user?.id ? 'text-orange-100' : 'text-gray-400'
                            }`}>
                              <span className="text-xs">{formatTime(msg.created_at)}</span>
                              {msg.sender_id === user?.id && (
                                msg.is_read 
                                  ? <CheckCheck className="w-3 h-3" />
                                  : <Check className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t bg-white">
                    <div className="flex gap-2">
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 resize-none"
                        rows={2}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="bg-[#D65A31] hover:bg-[#B84A28] text-white self-end"
                      >
                        {sendingMessage ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MessagesPage;
